import React from "react";
import { cn } from "../../lib/utils";

/**
 * Card component - Main container
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 * @returns {React.Component} Card component
 */
const Card = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
));
Card.displayName = "Card";

/**
 * CardHeader component - Card header section
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 * @returns {React.Component} CardHeader component
 */
const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-4 pb-2", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

/**
 * CardTitle component - Card title
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 * @returns {React.Component} CardTitle component
 */
const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  >
    {children || "Untitled"}
  </h3>
));
CardTitle.displayName = "CardTitle";

/**
 * CardDescription component - Card description
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 * @returns {React.Component} CardDescription component
 */
const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

/**
 * CardContent component - Card main content
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 * @returns {React.Component} CardContent component
 */
const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-4 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

/**
 * CardFooter component - Card footer section
 *
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 * @returns {React.Component} CardFooter component
 */
const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-4 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };